import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { clarityService, ClarityConversationMessage } from '../../../services/clarityService';
import './AnalyticsChatbot.css';

const MAX_HISTORY_PAIRS = 5;

interface Message {
  id: string;
  type: 'user' | 'bot';
  text?: string;
  timestamp: Date;
}

interface Template {
  id: string;
  title: string;
  query: string;
  description: string;
  query_type: string;
  icon: string;
}

const SUGGESTED_QUESTIONS: Template[] = [
  {
    id: '1',
    title: 'How many active clients are there?',
    query: 'How many active clients are there?',
    description: 'Get active client count',
    query_type: 'summary',
    icon: 'user'
  },
  {
    id: '2',
    title: 'Show total portfolio value',
    query: 'What is the total portfolio value across all clients?',
    description: 'Get total portfolio value',
    query_type: 'summary',
    icon: 'dollar'
  },
  {
    id: '3',
    title: 'Asset allocation summary',
    query: 'Show me the overall asset allocation',
    description: 'View asset allocation',
    query_type: 'analysis',
    icon: 'chart'
  }
];

const AnalyticsChatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [showTemplates] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const conversationHistoryRef = useRef<ClarityConversationMessage[]>([]);
  const fullResponseRef = useRef('');

  useEffect(() => {
    loadTemplates();
    return () => { abortControllerRef.current?.abort(); };
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadTemplates = async () => {
    setTemplates(SUGGESTED_QUESTIONS);
  };

  const sendQuery = useCallback(async (query: string) => {
    if (!query.trim()) return;

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      text: query,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    fullResponseRef.current = '';

    const botId = (Date.now() + 1).toString();
    let isFirstToken = true;
    const historySnapshot = [...conversationHistoryRef.current];

    try {
      await clarityService.streamChat(
        query.trim(),
        historySnapshot,
        (token) => {
          fullResponseRef.current += token;
          if (isFirstToken) {
            isFirstToken = false;
            setLoading(false);
            setMessages(prev => [...prev, {
              id: botId,
              type: 'bot',
              text: token,
              timestamp: new Date(),
            }]);
          } else {
            setMessages(prev => prev.map(msg =>
              msg.id === botId ? { ...msg, text: (msg.text ?? '') + token } : msg
            ));
          }
        },
        abortControllerRef.current.signal,
      );

      if (isFirstToken) {
        setMessages(prev => [...prev, {
          id: botId,
          type: 'bot',
          text: 'Sorry, I could not process your request. Please try again.',
          timestamp: new Date(),
        }]);
      } else {
        conversationHistoryRef.current = [
          ...conversationHistoryRef.current,
          { role: 'user' as const, content: query.trim() },
          { role: 'assistant' as const, content: fullResponseRef.current.trim() },
        ].slice(-MAX_HISTORY_PAIRS * 2);
      }
    } catch (error) {
      if ((error as Error).name === 'AbortError') return;
      console.error('Error sending query:', error);
      setMessages(prev => [...prev, {
        id: botId,
        type: 'bot',
        text: 'Sorry, I encountered an error connecting to the service. Please try again.',
        timestamp: new Date(),
      }]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleTemplateClick = (template: Template) => {
    sendQuery(template.query);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendQuery(input);
    }
  };

  const getIcon = (iconName: string) => {
    const iconMap: Record<string, string> = {
      'trend': '📈',
      'bar-chart': '📊',
      'gauge': '⚡',
      'chart': '📉',
      'user': '👤',
      'dollar': '💰',
    };
    return iconMap[iconName] || '✨';
  };

  return (
    <>
      {/* FAB Button */}
      <button
        className="chatbot-fab"
        onClick={() => setIsOpen(!isOpen)}
        title="Clarity - AI Assistant for Client Converge"
        style={{ opacity: isOpen ? 0 : 1 }}
      >
        <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g filter="url(#filter0_d_clarity)">
                <rect x="12" y="12" width="56" height="56" rx="12" fill="url(#paint0_linear_clarity)"/>
                <path d="M 49 28 C 42 28 37 33 37 40 C 37 47 42 52 49 52" stroke="white" stroke-width="7" stroke-linecap="round" fill="none"/>
                <path d="M 54 31 L 55.2 33.5 L 57.7 34.7 L 55.2 35.9 L 54 38.4 L 52.8 35.9 L 50.3 34.7 L 52.8 33.5 Z" fill="white" opacity="0.95"/>
                <path d="M 28 46 L 29 48 L 31 49 L 29 50 L 28 52 L 27 50 L 25 49 L 27 48 Z" fill="white" opacity="0.9"/>
                <path d="M 56 48 L 56.7 49.5 L 58.2 50.2 L 56.7 50.9 L 56 52.4 L 55.3 50.9 L 53.8 50.2 L 55.3 49.5 Z" fill="white" opacity="0.9"/>
                <path d="M 30 30 L 30.5 31 L 31.5 31.5 L 30.5 32 L 30 33 L 29.5 32 L 28.5 31.5 L 29.5 31 Z" fill="white" opacity="0.75"/>
            </g>
            <defs>
                <filter id="filter0_d_clarity" x="0" y="0" width="80" height="80" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                    <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                    <feOffset/>
                    <feGaussianBlur stdDeviation="6"/>
                    <feComposite in2="hardAlpha" operator="out"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.32 0"/>
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_clarity"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_clarity" result="shape"/>
                </filter>
                <linearGradient id="paint0_linear_clarity" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop stop-color="#0C649B"/>
                    <stop offset="1" stop-color="#094A73"/>
                </linearGradient>
            </defs>
        </svg>
      </button>

      {/* Chatbot Modal */}
      <div className={`chatbot-modal ${isOpen ? 'show' : ''}`}>
        <div className="chatbot-container">
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-content">
              <div className="chatbot-title">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 6V4H10M14 10V12M6 11H8M18 11H20M10 10V12M18 15C18 15.5304 17.7893 16.0391 17.4142 16.4142C17.0391 16.7893 16.5304 17 16 17H10.828C10.2976 17.0001 9.78901 17.2109 9.41405 17.586L7.212 19.788C7.11274 19.8873 6.98623 19.9549 6.84852 19.9823C6.71081 20.0097 6.56796 19.9956 6.43829 19.9419C6.30862 19.8881 6.19768 19.7971 6.11967 19.6804C6.04166 19.5637 6.00001 19.4264 6 19.286V9C6 8.46957 6.21071 7.96086 6.58579 7.58579C6.96086 7.21071 7.46957 7 8 7H16C16.5304 7 17.0391 7.21071 17.4142 7.58579C17.7893 7.96086 18 8.46957 18 9V15Z" stroke="#2563eb" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <div className="chatbot-title-text">
                  <span className="chatbot-brand-name">Clarity</span>
                  <span className="chatbot-app-name">by Client Converge</span>
                </div>
              </div>
              <button className="chatbot-close" onClick={() => setIsOpen(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L12 12M12 12L6 18M12 12L18 18M12 12L6 6" stroke="#020618" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="chatbot-body">
            <div className="chat-messages-container">
              <div className="chat-messages">
                {/* Template Suggestions - Sticky at top of messages */}
                {showTemplates && templates.length > 0 && (
                  <div className="suggested-questions-sticky">
                    <div className="bot-icon">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 4.5V2.5H7M10.5 7.5V9M4.5 8H6M13.5 8H15M7.5 7.5V9M13.5 11.5C13.5 11.8978 13.342 12.2794 13.0607 12.5607C12.7794 12.842 12.3978 13 12 13H7.621C7.22323 13.0001 6.84171 13.1581 6.56055 13.4395L4.909 15.091C4.83454 15.1655 4.73974 15.2165 4.63643 15.2373C4.53313 15.2582 4.42601 15.2481 4.32872 15.2082C4.23142 15.1682 4.14833 15.1003 4.08978 15.0129C4.03124 14.9254 4.00001 14.8226 4 14.7175V7C4 6.60218 4.15804 6.22064 4.43934 5.93934C4.72064 5.65804 5.10218 5.5 5.5 5.5H12C12.3978 5.5 12.7794 5.65804 13.0607 5.93934C13.342 6.22064 13.5 6.60218 13.5 7V11.5Z" stroke="#2563eb" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                    <div className="suggested-questions-sticky-content">
                      <div className="message-text">Hi 👋 I'm <strong>Clarity</strong>, your AI assistant for Client Converge. I can help you analyze portfolios, asset allocation, performance trends, and more. What would you like to explore?</div>
                      {templates.map(template => (
                        <button
                          key={template.id}
                          className="suggested-question"
                          onClick={() => handleTemplateClick(template)}
                        >
                          <span style={{ marginRight: '8px' }}>{getIcon(template.icon)}</span>
                          {template.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {messages.length === 0 && showTemplates && (
                  <div className="empty-state-message">
                    <p>Ask Clarity about your portfolio data!</p>
                  </div>
                )}

                {messages.map(msg => (
                  <div key={msg.id} className={`chat-message ${msg.type}-message`}>
                    {msg.type === 'bot' && (
                      <div className="bot-icon">
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 4.5V2.5H7M10.5 7.5V9M4.5 8H6M13.5 8H15M7.5 7.5V9M13.5 11.5C13.5 11.8978 13.342 12.2794 13.0607 12.5607C12.7794 12.842 12.3978 13 12 13H7.621C7.22323 13.0001 6.84171 13.1581 6.56055 13.4395L4.909 15.091C4.83454 15.1655 4.73974 15.2165 4.63643 15.2373C4.53313 15.2582 4.42601 15.2481 4.32872 15.2082C4.23142 15.1682 4.14833 15.1003 4.08978 15.0129C4.03124 14.9254 4.00001 14.8226 4 14.7175V7C4 6.60218 4.15804 6.22064 4.43934 5.93934C4.72064 5.65804 5.10218 5.5 5.5 5.5H12C12.3978 5.5 12.7794 5.65804 13.0607 5.93934C13.342 6.22064 13.5 6.60218 13.5 7V11.5Z" stroke="#2563eb" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                      </div>
                    )}
                    <div className="message-wrapper">
                      <div className="message-content">
                        {msg.type === 'bot'
                          ? <ReactMarkdown>{msg.text ?? ''}</ReactMarkdown>
                          : <span>{msg.text}</span>}
                      </div>
                      <div className="message-time">
                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
                      </div>
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="chat-message bot-message">
                    <div className="bot-icon">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 4.5V2.5H7M10.5 7.5V9M4.5 8H6M13.5 8H15M7.5 7.5V9M13.5 11.5C13.5 11.8978 13.342 12.2794 13.0607 12.5607C12.7794 12.842 12.3978 13 12 13H7.621C7.22323 13.0001 6.84171 13.1581 6.56055 13.4395L4.909 15.091C4.83454 15.1655 4.73974 15.2165 4.63643 15.2373C4.53313 15.2582 4.42601 15.2481 4.32872 15.2082C4.23142 15.1682 4.14833 15.1003 4.08978 15.0129C4.03124 14.9254 4.00001 14.8226 4 14.7175V7C4 6.60218 4.15804 6.22064 4.43934 5.93934C4.72064 5.65804 5.10218 5.5 5.5 5.5H12C12.3978 5.5 12.7794 5.65804 13.0607 5.93934C13.342 6.22064 13.5 6.60218 13.5 7V11.5Z" stroke="#2563eb" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                    <div className="typing-indicator">
                      <span className="typing-dot"></span>
                      <span className="typing-dot"></span>
                      <span className="typing-dot"></span>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>
          </div>

          {/* Input */}
          <div className="chat-input-container">
            <div className="chat-input-wrapper">
              <input
                type="text"
                className="chat-input"
                placeholder="Ask Clarity about portfolios, analytics..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
              <button
                className="chat-send-btn"
                onClick={() => sendQuery(input)}
                disabled={loading || !input.trim()}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16.6915026,12.4744748 L3.50612381,13.2599618 C3.19218622,13.2599618 3.03521743,13.4170592 3.03521743,13.5741566 L1.15159189,20.0151496 C0.8376543,20.8006365 0.99,21.89 1.77946707,22.52 C2.41,22.99 3.50612381,23.1 4.13399899,22.8429026 L21.714504,14.0454487 C22.6563168,13.5741566 23.1272231,12.6315722 22.9702544,11.6889879 L4.13399899,1.16346272 C3.34915502,0.9 2.40734225,0.99 1.77946707,1.4 C0.994623095,2.0 0.837654326,3.0942439 1.15159189,3.87786219 L3.03521743,10.3188551 C3.03521743,10.4759526 3.34915502,10.6330501 3.50612381,10.6330501 L16.6915026,11.4185369 C16.6915026,11.4185369 17.1624089,11.4185369 17.1624089,11.9897481 L17.1624089,12.0015035 C17.1624089,12.4744748 16.6915026,12.4744748 16.6915026,12.4744748 Z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AnalyticsChatbot;
